/**
 * Safe Error Rendering Utilities
 * 
 * Provides utilities to safely render error messages in React components,
 * preventing "Objects are not valid as a React child" errors.
 */

import { extractErrorMessage } from './normalizeError';

/**
 * Safely render an error message, handling both strings and objects
 * @param {string|object|array} error - The error to render
 * @returns {string} Safe string representation of the error
 */
export const safeErrorRender = (error) => {
    return extractErrorMessage(error);
};

/**
 * Safe error component for rendering field validation errors
 * @param {object} props - Component props
 * @param {string|object|array} props.error - The error to render
 * @param {string} props.className - CSS class name (optional)
 * @param {React.ReactNode} props.children - Children to render if no error (optional)
 * @returns {React.ReactNode} Safe error display component
 */
export const SafeErrorDisplay = ({ error, className = 'text-danger', children }) => {
    if (!error) {
        return children || null;
    }
    
    const message = safeErrorRender(error);
    if (!message) {
        return children || null;
    }
    
    return <div className={className}>{message}</div>;
};

/**
 * Safe error feedback for Bootstrap forms
 * @param {object} props - Component props  
 * @param {string|object|array} props.error - The error to render
 * @param {string} props.type - Bootstrap feedback type ('invalid' or 'valid')
 * @returns {React.ReactNode} Safe Bootstrap feedback component
 */
export const SafeFormFeedback = ({ error, type = 'invalid' }) => {
    if (!error) {
        return null;
    }
    
    const message = safeErrorRender(error);
    if (!message) {
        return null;
    }
    
    return (
        <div className={`${type}-feedback`} style={{ display: 'block' }}>
            {message}
        </div>
    );
};

/**
 * Get multiple field errors as safe strings
 * @param {object} errors - Object containing field errors
 * @param {string[]} fields - Array of field names to extract
 * @returns {object} Object with safe string error messages
 */
export const getSafeFieldErrors = (errors, fields) => {
    if (!errors || typeof errors !== 'object') {
        return {};
    }
    
    const safeErrors = {};
    fields.forEach(field => {
        if (errors[field]) {
            safeErrors[field] = safeErrorRender(errors[field]);
        }
    });
    
    return safeErrors;
};

/**
 * Check if an error exists and has a displayable message
 * @param {any} error - The error to check
 * @returns {boolean} True if error has a displayable message
 */
export const hasDisplayableError = (error) => {
    const message = safeErrorRender(error);
    return message && message.trim().length > 0;
};

/**
 * Create a safe error getter function for Formik components
 * Use this pattern in render prop callbacks to avoid hook rule violations
 * @param {object} errors - Formik errors object
 * @param {object} touched - Formik touched object
 * @returns {function} Function to get safe error message for a field
 */
export const createSafeErrorGetter = (errors, touched) => {
    return (fieldName) => {
        if (!touched[fieldName] || !errors[fieldName]) {
            return null;
        }
        return safeErrorRender(errors[fieldName]);
    };
};

export default {
    safeErrorRender,
    SafeErrorDisplay,
    SafeFormFeedback,
    getSafeFieldErrors,
    hasDisplayableError,
    createSafeErrorGetter
};